// utils/AuthUtils.js

import { 
  getAuth, 
  createUserWithEmailAndPassword, 
  updateProfile,
  signInWithEmailAndPassword,
  signOut
} from "firebase/auth";

/**
 * Crea un usuario en Firebase Authentication
 * @param {string} email - Email del usuario (puede ser generado)
 * @param {string} password - Contraseña del usuario
 * @param {string} displayName - Nombre completo del usuario
 * @returns {Promise<Object>} - Resultado de la operación
 */
export const createFirebaseAuthUser = async (email, password, displayName) => {
  try {
    // Si no hay email, generar uno temporal usando tipVS o DNI
    const auth = getAuth();
    
    // Mantener la sesión actual
    const currentUser = auth.currentUser;
    
    // Guardar datos de sesión actual si existe
    let currentUserEmail = null;
    let currentUserPassword = null;
    
    if (currentUser) {
      // Este es un placeholder - en una aplicación real no deberías
      // almacenar contraseñas, sino usar tokens o métodos más seguros
      currentUserEmail = sessionStorage.getItem("admin_email");
      currentUserPassword = sessionStorage.getItem("admin_password");
      
      // Cerrar sesión del usuario actual
      await signOut(auth);
    }
    
    // Crear el nuevo usuario
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Actualizar el perfil con el nombre completo
    if (displayName) {
      await updateProfile(user, {
        displayName: displayName
      });
    }
    
    // Cerrar sesión del usuario recién creado
    await signOut(auth);
    
    // Volver a iniciar sesión con el usuario administrador si existía
    if (currentUserEmail && currentUserPassword) {
      await signInWithEmailAndPassword(auth, currentUserEmail, currentUserPassword);
    }
    
    return {
      success: true,
      uid: user.uid,
      email: user.email
    };
  } catch (error) {
    console.error("Error creando usuario en Firebase Auth:", error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Verifica si un vigilante necesita ser registrado en Firebase Auth
 * basado en la presencia de email y authUID
 * @param {Object} vigilanteData - Datos del vigilante
 * @returns {Object} - Estado de registro
 */
export const verifyAuthRegistration = (vigilanteData) => {
  // Si no hay datos, no podemos verificar
  if (!vigilanteData) {
    return { needsAuth: false, authStatus: 'unknown' };
  }
  
  // Si ya tiene authUID, está registrado
  if (vigilanteData.authUID) {
    return { needsAuth: false, authStatus: 'registered' };
  }
  
  // Si tiene email pero no authUID, asumimos que está registrado en Auth
  // pero necesita actualización en Firestore
  if (vigilanteData.email && vigilanteData.email.trim() !== '') {
    return { 
      needsAuth: false, 
      authStatus: 'needs_update',
      message: "El vigilante ya está registrado en Firebase Auth, pero falta el ID en Firestore."
    };
  }
  
  // Si no tiene email ni authUID, necesita ser registrado
  return { 
    needsAuth: true, 
    authStatus: 'not_registered',
    message: "El vigilante no está registrado en Firebase Authentication."
  };
};

/**
 * Genera un email único para usuarios sin email
 * @param {string} tipVS - Número de identificación del vigilante
 * @param {string} serviceId - ID del servicio
 * @returns {string} - Email generado
 */
export const generateUniqueEmail = (tipVS, serviceId) => {
  // Crear un email basado en tipVS y un timestamp para garantizar unicidad
  const timestamp = new Date().getTime();
  return `vigilante_${tipVS}_${serviceId.substring(0, 6)}_${timestamp}@sinregistro.local`;
};